import { Controller } from '@hotwired/stimulus'

// The one method this controller calls on the <turbo-frame> element.
type ReloadableFrame = HTMLElement & { reload: () => void }

type BeforeFrameRenderEvent = CustomEvent<{
  render: (currentFrame: Element, newFrame: Element) => void
}>

export default class extends Controller {
  static values = {
    interval: { type: Number, default: 10000 },
    duration: { type: Number, default: 120000 },
  }

  static targets = ['frame']

  declare readonly intervalValue: number
  declare readonly durationValue: number
  declare readonly frameTarget: ReloadableFrame

  private timer?: number
  private pollUntil = 0

  connect(): void {
    this.extendPolling()
    this.scheduleNextReload()
    this.frameTarget.addEventListener(
      'turbo:before-frame-render',
      this.handleBeforeFrameRender
    )
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('focus', this.handleFocus)
  }

  disconnect(): void {
    window.clearTimeout(this.timer)
    this.frameTarget.removeEventListener(
      'turbo:before-frame-render',
      this.handleBeforeFrameRender
    )
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
    window.removeEventListener('focus', this.handleFocus)
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      window.clearTimeout(this.timer)
    } else {
      this.handleFocus()
    }
  }

  // A reload that started before the confirmation opened would still replace the form. Turbo
  // swaps the content one repaint after this event, so the check runs at the swap.
  private handleBeforeFrameRender = (event: Event): void => {
    const { detail } = event as BeforeFrameRenderEvent
    const render = detail.render
    detail.render = (currentFrame, newFrame): void => {
      if (!this.confirmationOpen) {
        render(currentFrame, newFrame)
      }
    }
  }

  // A user who comes back to the tab may have just connected an assistant, so the polling starts
  // again from there.
  private handleFocus = (): void => {
    this.extendPolling()
    this.reloadNow()
  }

  // Only a card that is not connected can change without a request from this page: a
  // disconnect posts a form and loads the page again.
  private get allConnected(): boolean {
    return (
      this.frameTarget.querySelector<HTMLElement>('[data-all-connected]')
        ?.dataset.allConnected === 'true'
    )
  }

  // The jquery-ujs confirmation keeps the Disconnect button that opened it and submits that
  // button's form on Yes. A reload replaces the form, and the browser does not submit a detached one.
  private get confirmationOpen(): boolean {
    return (
      document.querySelector(
        '[data-behaviour="global-confirmation-modal"]:not(.hidden)'
      ) !== null
    )
  }

  private extendPolling(): void {
    this.pollUntil = Date.now() + this.durationValue
  }

  private reloadNow = (): void => {
    // Coming back to the tab fires both focus and visibilitychange.
    if (
      !this.allConnected &&
      !this.confirmationOpen &&
      !this.frameTarget.hasAttribute('busy')
    ) {
      this.frameTarget.reload()
    }

    this.scheduleNextReload()
  }

  private scheduleNextReload(): void {
    window.clearTimeout(this.timer)
    if (document.hidden || this.allConnected || Date.now() >= this.pollUntil) {
      return
    }

    this.timer = window.setTimeout(this.reloadNow, this.intervalValue)
  }
}
